import { Building2 } from 'lucide-react';
import type { EventPartner, ResolvedCustomHomeSection } from '@conference/shared';
import { useNavigate } from 'react-router';
import HomeSectionMarkdown from '../../components/HomeSectionMarkdown';
import { getModuleDestination } from '../../utils/moduleNavigation';
import { apiAssetUrl } from '../../services/apiClient';

export default function CustomHomeSectionCard({ section, featuredSponsors = [] }: {
  section: ResolvedCustomHomeSection;
  featuredSponsors?: EventPartner[];
}) {
  const navigate = useNavigate();
  const internalPath = section.action?.kind === 'internal_module'
    ? getModuleDestination(section.action.module_key)
    : null;

  return (
    <section className="space-y-3" style={{ order: section.order }}>
      <h3 className="text-lg font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>{section.title}</h3>
      <article
        className="overflow-hidden rounded-xl border"
        style={{
          background: 'var(--app-surface)',
          borderColor: 'var(--app-border)',
          boxShadow: 'var(--app-card-shadow)',
        }}
      >
        {section.image_url && (
          <img src={apiAssetUrl(section.image_url) ?? undefined} alt={section.image_alt?.trim() || section.title} className="max-h-80 w-full object-contain" style={{ background: 'var(--app-surface-2)' }} />
        )}
        <div className="space-y-3 p-4">
          {section.content_style === 'featured_sponsors' ? (
            <div className="space-y-2">
              {featuredSponsors.map((sponsor) => (
                <button
                  key={sponsor.id}
                  type="button"
                  aria-label={`View ${sponsor.display_name}`}
                  onClick={() => navigate(`/exhibitor/${sponsor.id}`)}
                  className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left active:opacity-80"
                  style={{ background: 'var(--app-surface-2)' }}
                >
                  <span className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white p-1">
                    {sponsor.logo_url
                      ? <img src={apiAssetUrl(sponsor.logo_url) ?? undefined} alt={sponsor.logo_alt_text ?? `${sponsor.display_name} logo`} className="size-full object-contain" />
                      : <Building2 className="size-5" style={{ color: 'var(--app-accent)' }} />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-snug" style={{ color: 'var(--app-text)' }}>{sponsor.display_name}</span>
                </button>
              ))}
            </div>
          ) : <HomeSectionMarkdown markdown={section.body_markdown} />}
          {section.action?.kind === 'internal_module' && internalPath && (
            <button
              type="button"
              onClick={() => navigate(internalPath)}
              className="rounded-lg px-4 py-2 text-sm font-medium text-white active:opacity-80"
              style={{ background: 'var(--app-accent-solid)' }}
            >
              {section.action.label}
            </button>
          )}
          {section.action?.kind === 'external_url' && (
            <a
              href={section.action.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg px-4 py-2 text-sm font-medium text-white active:opacity-80"
              style={{ background: 'var(--app-accent-solid)' }}
            >
              {section.action.label}
            </a>
          )}
        </div>
      </article>
    </section>
  );
}
